import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { formatRole } from '../../core/utils/role-formatter';
import { WorkspaceAccesible } from '../../core/services/workspace-access.service';

/**
 * Tarjeta de un workspace. Se usa en dos lugares con la misma forma: la
 * pantalla de selección al iniciar sesión y el modal de cambio, que sólo se
 * diferencian en si muestran el botón de entrar o una marca de selección.
 */
@Component({
  selector: 'app-workspace-option',
  imports: [MatIconModule, TranslateModule],
  templateUrl: './workspace-option.html',
  styleUrl: './workspace-option.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspaceOption {
  private translate = inject(TranslateService);
  /** Para que la etiqueta se rearme al cambiar de idioma. */
  private idioma = toSignal(this.translate.onLangChange, { initialValue: null });

  readonly workspace = input.required<WorkspaceAccesible>();
  readonly modo = input<'seleccion' | 'cambio'>('seleccion');
  readonly seleccionado = input(false);
  readonly activo = input(false);

  readonly elegir = output<WorkspaceAccesible>();

  protected readonly esModoCambio = computed(() => this.modo() === 'cambio');

  /** Inicial para el caso, hoy el más común, de un workspace sin logo. */
  protected readonly inicial = computed(() => {
    const nombre = this.workspace().name?.trim();
    return nombre?.length ? nombre.charAt(0).toUpperCase() : 'W';
  });

  /**
   * Era la sexta copia del mapa de roles, y la única que ni siquiera traducía:
   * el nombre salía en español con la aplicación en cualquier idioma.
   */
  protected readonly etiquetaRol = computed(() => {
    this.idioma();
    const rol = this.workspace().rol;
    return rol ? formatRole(rol, this.translate) : '';
  });

  protected alElegir(): void {
    this.elegir.emit(this.workspace());
  }
}
